import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice
} from '@reduxjs/toolkit';

import {
  fetchPeaks as fetchPeaksApi
} from 'api/peaks';

// Slice
export const peaksAdapter = createEntityAdapter();
const initialState = peaksAdapter.getInitialState({
  pendingRequests: []
});

// The waveform only renders at this zoom, so finer data is wasted work.
export const PEAKS_SAMPLES_PER_PIXEL = 256;

// Peaks are generated at 64 samples per pixel. Merging them down to the one
// zoom level we render cuts long files' data 4x before it hits the store,
// Wave.js and Peaks.js, which would otherwise resample it again.
export const downsamplePeaks = (peaks, samplesPerPixel = PEAKS_SAMPLES_PER_PIXEL) => {
  const {data, samples_per_pixel: sourceSamplesPerPixel, channels = 1} = peaks || {};
  if (!data || !data.length || !sourceSamplesPerPixel) return peaks;

  const factor = Math.floor(samplesPerPixel / sourceSamplesPerPixel);
  if (factor <= 1) return peaks;

  const pointSize = channels * 2;
  const sourceLength = Math.floor(data.length / pointSize);
  const length = Math.ceil(sourceLength / factor);
  const downsampled = new Array(length * pointSize);

  for (let point = 0; point < length; point++) {
    const first = point * factor;
    const last = Math.min(first + factor, sourceLength);

    for (let channel = 0; channel < channels; channel++) {
      let min = Infinity;
      let max = -Infinity;

      for (let source = first; source < last; source++) {
        const offset = source * pointSize + channel * 2;
        if (data[offset] < min) min = data[offset];
        if (data[offset + 1] > max) max = data[offset + 1];
      }

      downsampled[point * pointSize + channel * 2] = min;
      downsampled[point * pointSize + channel * 2 + 1] = max;
    }
  }

  return {
    ...peaks,
    samples_per_pixel: sourceSamplesPerPixel * factor,
    length,
    data: downsampled
  };
};

export const fetchPeaks = createAsyncThunk(
  'peaks/fetch',
  async ({collectionId, peaksUrl, locale}) => {
    try {
      const {data} = await fetchPeaksApi({collectionId, peaksUrl, locale});
      return downsamplePeaks(data);
    } catch (error) {
      console.log(error);
      return {};
    }
  }
);

const sliceSelector = state => state.peaks;
const {selectById} = peaksAdapter.getSelectors(sliceSelector);
export const selectPeaksById = selectById;

export const selectCollectionPeaks = createSelector(
  [selectPeaksById],
  peak => peak
);

export const slice = createSlice({
  name: 'peaks',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchPeaks.fulfilled, (state, args) => {
        const {meta, payload} = args;
        // console.log({args})
        const {arg: {collectionId: id}} = meta;
        peaksAdapter.upsertOne(state, {id, ...payload});
      });
  }
});

const reducer = slice.reducer;
export default reducer;
